class ReturnForm extends HTMLElement {
  constructor() {
    super();
    this.form = null;
    this.tags = [];
  }

  connectedCallback() {
    this.form = this.querySelector("#exchange-refund");
    this.messageEl = this.querySelector("[data-message]");
    this.tagContainer = this.querySelector("[data-tag-container]");
    this.tagInput = this.querySelector("[data-tag-input]");
    this.productsHidden = this.querySelector("[data-products-hidden]");
    this.tagAddBtn = this.querySelector("[data-tag-add]");
    this.submitBtn = this.querySelector(".return-form__submit");
    this.btnText = this.querySelector("[data-button-text]");
    this.btnLoader = this.querySelector("[data-button-loader]");
    this.bindEvents();
  }

  bindEvents() {
    this.form.addEventListener("submit", this.handleSubmit.bind(this));
    this.tagInput.addEventListener("keydown", this.handleTagKeydown.bind(this));
    this.tagInput.addEventListener("input", this.handleTagInput.bind(this));
    this.tagInput.addEventListener("blur", this.commitPendingInput.bind(this));
    this.tagAddBtn.addEventListener("click", this.commitPendingInput.bind(this));
    this.tagContainer.addEventListener(
      "click",
      this.handleTagContainerClick.bind(this),
    );
  }

  handleTagKeydown(e) {
    if (e.key === "Enter") {
      e.preventDefault();
      this.commitPendingInput();
    }
  }

  handleTagInput() {
    if (this.tagInput.value.includes(",")) {
      this.commitPendingInput();
    }
  }

  commitPendingInput() {
    const raw = this.tagInput.value;
    if (!raw.trim()) return;

    raw.split(",").forEach((val) => {
      const text = val.trim();
      if (text) this.addTag(text);
    });
    this.tagInput.value = "";
  }

  addTag(text) {
    this.tags.push(text);
    this.syncProductsHidden();

    const tag = document.createElement("div");
    tag.classList.add("tag");

    const label = document.createElement("span");
    label.textContent = text;
    tag.appendChild(label);

    const removeBtn = document.createElement("span");
    removeBtn.classList.add("tag__remove");
    removeBtn.textContent = "\u00d7";
    removeBtn.setAttribute("role", "button");
    removeBtn.setAttribute("aria-label", `Remove ${text}`);
    removeBtn.addEventListener("click", () => this.removeTag(tag, text));
    tag.appendChild(removeBtn);

    this.tagContainer.insertBefore(tag, this.tagInput);
  }

  removeTag(tagEl, text) {
    tagEl.remove();
    this.tags = this.tags.filter((t) => t !== text);
    this.syncProductsHidden();
  }

  syncProductsHidden() {
    this.productsHidden.value = this.tags.join(", ");
  }

  handleTagContainerClick(e) {
    if (e.target === this.tagContainer || e.target === this.tagInput) {
      this.tagInput.focus();
    }
  }

  validate() {
    this.clearErrors();
    const errors = [];
    const MAX_FILES = 5;
    const MAX_FILE_SIZE = 5 * 1024 * 1024;
    const ALLOWED_TYPES = [
      "image/jpeg",
      "image/png",
      "image/webp",
      "image/gif",
    ];

    const required = [
      { name: "order_number", label: "Order Number" },
      { name: "email", label: "Email" },
      { name: "reason", label: "Reason" },
    ];

    required.forEach(({ name, label }) => {
      const field = this.form.querySelector(`[name="${name}"]`);
      if (!field.value.trim()) {
        errors.push(`${label} is required`);
        field.classList.add("input--error");
      }
    });

    const email = this.form.querySelector('[name="email"]');
    if (
      email.value.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim())
    ) {
      errors.push("Please enter a valid email address");
      email.classList.add("input--error");
    }

    if (this.tags.length === 0) {
      errors.push("Please add at least one product");
      this.tagContainer.classList.add("input--error");
    }

    const fileInput = this.form.querySelector('[name="images"]');
    if (fileInput.files.length > MAX_FILES) {
      errors.push(`Maximum ${MAX_FILES} images allowed`);
      fileInput.classList.add("input--error");
    }
    Array.from(fileInput.files).forEach((file) => {
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`File "${file.name}" exceeds the 5 MB limit`);
        fileInput.classList.add("input--error");
      }
      if (!ALLOWED_TYPES.includes(file.type)) {
        errors.push(`File "${file.name}" is not an allowed image type`);
        fileInput.classList.add("input--error");
      }
    });

    const policy = this.form.querySelector('[name="policy_agreed"]');
    if (!policy.checked) {
      errors.push("You must agree to the return policy");
      policy.classList.add("input--error");
    }

    return errors;
  }

  clearErrors() {
    this.querySelectorAll(".input--error").forEach((el) =>
      el.classList.remove("input--error"),
    );
  }

  showMessage(type, messages) {
    this.messageEl.hidden = false;
    this.messageEl.className = `return-form__message return-form__message--${type}`;

    const list = Array.isArray(messages) ? messages : [messages];
    if (list.length === 1) {
      this.messageEl.textContent = list[0];
    } else {
      this.messageEl.textContent = "";

      const ul = document.createElement("ul");
      ul.classList.add("return-form__error-list");
      list.forEach((msg) => {
        const li = document.createElement("li");
        li.textContent = msg;
        ul.appendChild(li);
      });
      this.messageEl.appendChild(ul);
    }

    this.messageEl.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  hideMessage() {
    this.messageEl.hidden = true;
    this.messageEl.textContent = "";
    this.messageEl.className = "return-form__message";
  }

  setLoading(isLoading) {
    this.submitBtn.disabled = isLoading;
    this.btnText.hidden = isLoading;
    this.btnLoader.hidden = !isLoading;
  }

  collectFormData() {
    const formData = new FormData(this.form);
    const data = new FormData();

    data.append("request_type", formData.get("request_type"));
    data.append("order_number", formData.get("order_number"));
    data.append("customer_name", formData.get("customer_name"));
    data.append("phone", formData.get("phone"));
    data.append("email", formData.get("email"));
    data.append("products", this.productsHidden.value);
    data.append("reason", formData.get("reason"));
    data.append("comments", formData.get("comments") || "");
    data.append("policy_agreed", formData.get("policy_agreed") || "");

    const fileInput = this.form.querySelector('[name="images"]');
    if (fileInput.files.length > 0) {
      Array.from(fileInput.files).forEach((file) =>
        data.append("images[]", file),
      );
    }

    return data;
  }

  async handleSubmit(e) {
    e.preventDefault();
    this.hideMessage();
    this.commitPendingInput();

    const errors = this.validate();
    if (errors.length > 0) {
      this.showMessage("error", errors);
      return;
    }

    // if (!this.endpoint) {
    //   this.showMessage(
    //     "error",
    //     "Form endpoint is not configured. Please contact the store administrator.",
    //   );
    //   return;
    // }

    this.setLoading(true);

    try {
      const data = this.collectFormData();
      const response = await fetch(
        "https://apps.netrategy.com/sreeleathersapp/api/request.php",
        {
          method: "POST",
          body: data,
        },
      );

      const result = await response.json().catch(() => null);

      if (response.ok && result?.success) {
        const requestId = result.request_id ? `#${result.request_id}` : "";
        const msg = requestId
          ? `Your request ${requestId} has been submitted successfully. Please save this ID for future reference. We will get back to you shortly.`
          : result.message || "Your request has been submitted successfully.";
        this.showMessage("success", msg);
        this.resetForm();
      } else {
        const errorMsg = result?.errors?.length
          ? result.errors
          : result?.message || "Something went wrong. Please try again later.";
        this.showMessage("error", errorMsg);
      }
    } catch {
      this.showMessage(
        "error",
        "Network error. Please check your connection and try again.",
      );
    } finally {
      this.setLoading(false);
    }
  }

  resetForm() {
    this.form.reset();
    this.tags = [];
    this.syncProductsHidden();
    this.tagContainer.querySelectorAll(".tag").forEach((tag) => tag.remove());
    this.clearErrors();
  }
}

customElements.define("return-form", ReturnForm);
